<template>
  <Row>
    <i-col span="24">
      <AffixHeader>
        <BreadcrumbItem slot="parent" to="/facts">Facts</BreadcrumbItem>
        <span slot="current">{{ breadcrumb }}</span>
        <span slot="title">{{ title }}</span>
        <span slot="sub-title">{{ fullname }}</span>
      </AffixHeader>

      <i-form ref="form" :model="fact" :rules="rules" label-position="top">
        <Tabs v-model="activeTab" :animated="false">
          <!-- Tab: Main -->
          <TabPane label="Main">
            <!-- Header: Key Information -->
            <Card dis-hover>
              <p slot="title">
                <FFIcon type="leanpub" />Key Information
              </p>

              <!-- Name and Serial No. -->
              <i-form-item label="Name" prop="name">
                <i-input v-model="fact.name"></i-input>
              </i-form-item>

              <!-- Country & Year -->
              <Row>
                <i-col span="12">
                  <i-form-item label="Country" prop="iso3" style="padding-right:6px;">
                    <CountrySelect v-model="fact.iso3" filterable></CountrySelect>
                  </i-form-item>
                </i-col>
                <i-col span="12">
                  <i-form-item label="Year" prop="year">
                    <i-select v-model.number="fact.year" filterable>
                      <i-option v-for="year in years" :key="year" :value="year">{{ year }}</i-option>
                    </i-select>
                  </i-form-item>
                </i-col>
              </Row>

              <!-- Displacement Type & Fact Type -->
              <Row>
                <i-col span="12">
                  <i-form-item
                    label="Displacement Type"
                    prop="displacement_type"
                    style="padding-right:6px;"
                  >
                    <i-select v-model="fact.displacement_type" filterable>
                      <i-option value="Conflict">Conflict</i-option>
                      <i-option value="Disaster">Disaster</i-option>
                      <i-option value="Development">Development</i-option>
                    </i-select>
                  </i-form-item>
                </i-col>
                <i-col span="12">
                  <i-form-item label="Type" prop="type">
                    <FactsTypeSelect v-model="fact.type" filterable />
                  </i-form-item>
                </i-col>
              </Row>
            </Card>

            <!-- Reporting -->
            <Card dis-hover>
              <p slot="title">
                <FFIcon type="file" />Reporting
              </p>
              <Row>
                <!-- Reporting Dates -->
                <i-col span="12">
                  <i-form-item label="Start Date" style="padding-right:6px;">
                    <AccuracyDatePicker v-model="fact.start_date"></AccuracyDatePicker>
                  </i-form-item>
                </i-col>
                <i-col span="12">
                  <i-form-item label="End Date">
                    <AccuracyDatePicker v-model="fact.end_date"></AccuracyDatePicker>
                  </i-form-item>
                </i-col>
              </Row>
              <!-- Reporting Comment -->
              <i-form-item label="Comment" prop="comment">
                <i-input
                  v-model="fact.comment"
                  type="textarea"
                  spellcheck
                  :autosize="{ minRows: 6, maxRows: 36 }"
                  :rows="4"
                ></i-input>
              </i-form-item>
            </Card>

            <!-- Header: Association -->
            <Card dis-hover>
              <p slot="title">
                <FFIcon type="sitemap" />Association
              </p>

              <!-- Document -->
              <DocumentAssociation :id="fact.document_id" v-show="fact.document_id > 0"></DocumentAssociation>

              <!-- Master Fact -->
              <i-form-item label="Master Fact" v-if="fact.document_id !== null">
                <MasterFactAssociation
                  :id="fact.parent_id"
                  :role="fact.parent_role"
                  :conditions="{
                    iso3: fact.iso3,
                    type: fact.type,
                    displacement_type: fact.displacement_type,
                    year: fact.year,
                  }"
                  @change="
                    ({id, role}) => {
                      fact.parent_id = id;
                      fact.parent_role = role;
                    }
                  "
                ></MasterFactAssociation>
              </i-form-item>

              <!-- Event -->
              <i-form-item label="Event">
                <EventAssociation
                  :id="fact.event_id"
                  :role="fact.event_role"
                  @change="
                    ({ id, role }) => {
                      fact.event_id = id;
                      fact.event_role = role;
                    }
                  "
                ></EventAssociation>
              </i-form-item>

              <!-- Groups -->
              <i-form-item label="Groups">
                <GroupSelect v-model="fact.groups" />
              </i-form-item>

              <!-- Themes -->
              <i-form-item label="Themes">
                <ThemeSelect v-model="fact.themes" />
              </i-form-item>
            </Card>

            <!-- Header: Data -->
            <Card dis-hover>
              <p slot="title">
                <FFIcon type="database" />Data
              </p>
              <!-- Figures -->
              <!--
                <FigureFields :iso3="fact.iso3" :year="fact.year" :qualifier.sync="fact.data.qualifier" :reported_figure.sync="fact.data.reported_figure" :unit.sync="fact.data.unit" :figure.sync="fact.data.figure" :term.sync="fact.data.term" :household_size.sync="fact.household_size"></FigureFields>
              -->
              <Row>
                <Row>
                  <i-col span="6">
                    <i-form-item label="Qualifier" style="margin-right:6px;">
                      <i-select v-model="fact.data.qualifier" @on-change="calculate">
                        <i-option value="approximately">Approximately</i-option>
                        <i-option value="less than">Less than</i-option>
                        <i-option value="more than">More than</i-option>
                        <i-option value="total">Total</i-option>
                      </i-select>
                    </i-form-item>
                  </i-col>

                  <i-col span="4">
                    <i-form-item
                      label="Reported Figure"
                      style="margin-right:6px;"
                      prop="data.reported_figure"
                    >
                      <i-input
                        v-model.number="fact.data.reported_figure"
                        number
                        @on-change="calculate"
                      ></i-input>
                    </i-form-item>
                  </i-col>

                  <i-col span="4">
                    <i-form-item label="Unit" style="margin-right:6px;" prop="data.unit">
                      <i-select v-model="fact.data.unit" @on-change="calculate">
                        <i-option value="Person">Person</i-option>
                        <i-option value="Household">Household</i-option>
                      </i-select>
                    </i-form-item>
                  </i-col>

                  <i-col span="4">
                    <i-form-item label="Figure" style="margin-right:6px;" prop="data.figure">
                      <i-input v-model.number="fact.data.figure" readonly></i-input>
                    </i-form-item>
                  </i-col>

                  <i-col span="6">
                    <i-form-item label="Term" prop="data.term">
                      <i-select v-model="fact.data.term">
                        <i-option value="Displaced">Displaced</i-option>
                        <i-option value="Evacuated">Evacuated</i-option>
                        <i-option value="Destroyed Housing">Destroyed Housing</i-option>
                        <i-option value="Partially Destroyed Housing">Partially Destroyed Housing</i-option>
                        <i-option value="Uninhabitable Housing">Uninhabitable Housing</i-option>
                        <i-option value="Forced to Flee">Forced to Flee</i-option>
                        <i-option value="Homeless">Homeless</i-option>
                        <i-option value="In Relief Camp">In Relief Camp</i-option>
                        <i-option value="Sheltered">Sheltered</i-option>
                        <i-option value="Relocated">Relocated</i-option>
                        <i-option value="Affected">Affected</i-option>
                        <i-option value="Returns">Returns</i-option>
                        <i-option value="Multiple/Other">Multiple/Other</i-option>
                      </i-select>
                    </i-form-item>
                  </i-col>
                </Row>

                <Row>
                  <!-- Household Size -->
                  <i-form-item label="Household Size" v-show="fact.data.unit === 'Household'">
                    <HouseholdSize
                      v-model="fact.household_size"
                      :conditions="{ iso3: fact.iso3, year: fact.year }"
                      :iso3="fact.iso3"
                      :year="fact.year"
                      :recalculate="fact.data.unit === 'Household'"
                      @change="calculate"
                    ></HouseholdSize>
                  </i-form-item>
                </Row>
              </Row>
              <!--
                Figure Dates -----------------------------------------------------------------------------------------------
              -->
              <Row>
                <Row v-if="isFlowType">
                  <i-col span="12">
                    <i-form-item
                      label="Start Date"
                      :prop="isFlowType ? 'data.start_date.date' : ''"
                      style="padding-right:6px;"
                    >
                      <AccuracyDatePicker v-model="fact.data.start_date"></AccuracyDatePicker>
                    </i-form-item>
                  </i-col>
                  <i-col span="12">
                    <i-form-item label="End Date">
                      <AccuracyDatePicker v-model="fact.data.end_date"></AccuracyDatePicker>
                    </i-form-item>
                  </i-col>
                </Row>
                <Row v-else>
                  <i-col span="12">
                    <i-form-item
                      label="Stock Date"
                      :prop="isFlowType ? '' : 'data.stock_date.date'"
                    >
                      <AccuracyDatePicker v-model="fact.data.stock_date"></AccuracyDatePicker>
                    </i-form-item>
                  </i-col>
                </Row>
              </Row>
              <!--
                Figure Dates -----------------------------------------------------------------------------------------------
              -->
            </Card>

            <!-- Header: Geospatial -->
            <Card dis-hover>
              <p slot="title">
                <FFIcon type="map-o" />Geospatial
              </p>
              <i-form-item label="Location">
                <DynamicTags v-model="fact.data.location"></DynamicTags>
              </i-form-item>
              <i-form-item label="Locations">
                <GeolocationSelect v-model="fact.locations" :iso3="fact.iso3"></GeolocationSelect>
              </i-form-item>
            </Card>
          </TabPane>

          <!-- Tab: Tabular Data -->
          <TabPane label="Tabular Data" v-if="fact.id">
            <!-- <TabularDataForm
              v-on:updateSelectedRecords="updateSelectedRecords"
              :selectedRecords="selectedTabularRecords"
              :fact="fact"
              :recordsData="searchTabularRecords"
            ></TabularDataForm>-->
            <TabularDatasetTable :factID="fact.id" :hack="activeTab" :tabIndex="1"></TabularDatasetTable>
          </TabPane>

          <!-- Tab: Disaggregated Data -->
          <TabPane label="Disaggregated Data">
            <!-- Displacement Setting -->
            <Card dis-hover>
              <p slot="title">Displacement Setting</p>
              <Row>
                <i-col span="12">
                  <i-form-item label="Urban">
                    <i-input v-model.number="fact.data.urban" style="padding-right:6px;"></i-input>
                  </i-form-item>
                </i-col>
                <i-col span="12">
                  <i-form-item label="Rural">
                    <i-input v-model.number="fact.data.rural"></i-input>
                  </i-form-item>
                </i-col>
              </Row>
            </Card>
            <!-- Location Type -->
            <Card dis-hover>
              <p slot="title">Location Type</p>
              <Row>
                <i-col span="12">
                  <i-form-item label="Camp">
                    <i-input v-model.number="fact.data.camp" style="padding-right:6px;"></i-input>
                  </i-form-item>
                </i-col>
                <i-col span="12">
                  <i-form-item label="Non Camp">
                    <i-input v-model.number="fact.data.non_camp"></i-input>
                  </i-form-item>
                </i-col>
              </Row>
            </Card>
            <!-- Disaggregated by Sex -->
            <Card dis-hover>
              <p slot="title">Disaggregated by Sex</p>
              <Row>
                <i-col span="12">
                  <i-form-item label="Male">
                    <i-input v-model.number="fact.data.males" style="padding-right:6px;"></i-input>
                  </i-form-item>
                </i-col>
                <i-col span="12">
                  <i-form-item label="Female">
                    <i-input v-model.number="fact.data.females"></i-input>
                  </i-form-item>
                </i-col>
              </Row>
            </Card>

            <!-- Age Disaggregated Data -->
            <Card dis-hover>
              <p slot="title">Age Disaggregated Data</p>
              <AgeDisaggregatedData v-model="fact.data.age_disaggregated_data"></AgeDisaggregatedData>
            </Card>

            <!-- Strata Disaggregated Data -->
            <Card dis-hover>
              <p slot="title">Strata Disaggregated Data</p>
              <StrataDisaggregatedData v-model="fact.data.strata_disaggregated_data"></StrataDisaggregatedData>
            </Card>

            <!-- Conflict Type Disaggregated Data -->
            <Card dis-hover v-if="fact.displacement_type === 'Conflict'">
              <p slot="title">Conflict Type Disaggregated Data</p>
              <ConflictTypeDisaggregatedData v-model="fact.data.conflict_type_disaggregated_data"></ConflictTypeDisaggregatedData>
            </Card>
          </TabPane>

          <!-- Tab: Analysis -->
          <TabPane label="Analysis">
            <!-- Excerpt from source -->
            <i-form-item label="Excerpt from source">
              <i-input
                v-model="fact.data.excerpt"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="6"
              ></i-input>
            </i-form-item>

            <i-form-item label="Include in the IDU">
              <i-switch v-model="fact.idu" size="large">
                <span slot="open">Yes</span>
                <span slot="close">No</span>
              </i-switch>
            </i-form-item>

            <i-form-item label="Excerpt for IDU" prop="data.excerpt_for_idu">
              <i-input
                v-model="fact.data.excerpt_for_idu"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="6"
              ></i-input>
            </i-form-item>

            <!-- Sources breakdown and reliability -->
            <i-form-item label="Sources breakdown and reliability">
              <i-input
                v-model="fact.data.souces"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="6"
              ></i-input>
            </i-form-item>

            <!-- Analysis -->
            <i-form-item label="Analysis">
              <i-input
                v-model="fact.data.analysis"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="6"
              ></i-input>
            </i-form-item>

            <!-- Caveats -->
            <i-form-item label="Caveats">
              <i-input
                v-model="fact.data.caveats"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="6"
              ></i-input>
            </i-form-item>

            <!-- Significant changes -->
            <i-form-item label="Significant changes">
              <i-input
                v-model="fact.data.changes"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="6"
              ></i-input>
            </i-form-item>

            <!-- Partner engagement -->
            <i-form-item label="Partner engagement">
              <i-input
                v-model="fact.data.partners"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="6"
              ></i-input>
            </i-form-item>

            <!-- Public Figure analysis -->
            <i-form-item label="Public Figure analysis - for website">
              <i-input
                v-model="fact.data.public_figure_analysis"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="6"
              ></i-input>
            </i-form-item>
          </TabPane>

          <!-- Tab: Analysis -->
          <TabPane label="Confidence Assessment">
            <i-form-item>
              <i-switch v-model="confidenceAssessmentOn" size="large">
                <span slot="open">On</span>
                <span slot="close">Off</span>
              </i-switch>
            </i-form-item>
            <ConfidenceAssessmentForm
              v-model="fact.confidence_assessment"
              v-if="confidenceAssessmentOn"
            ></ConfidenceAssessmentForm>
          </TabPane>

          <TabPane label="Facts" :disabled="!(id > 0 && isMaster)">
            <SubFactsTable :hack="activeTab" :tabIndex="5" :factID="fact.id"></SubFactsTable>
          </TabPane>

          <TabPane label="Reviews">
            <Reviews type="facts" :id="id"></Reviews>
          </TabPane>
        </Tabs>

        <!-- Action Buttons -->
        <i-form-item class="action-buttons" v-show="$can('manage', 'facts') && !locked && true">
          <i-button :disabled="busy" type="primary" @click="save">
            <FFIcon type="save" />
            {{ isNew ? "Create" : "Update" }}
          </i-button>

          <Poptip
            confirm
            title="Are you sure?"
            @on-ok="$router.push(`/facts/clone/${fact.id}`)"
            v-if="!(isClone || isNew)"
          >
            <i-button :disabled="busy">
              <FFIcon type="copy" />Clone
            </i-button>
          </Poptip>

          <Poptip
            confirm
            title="Are you sure?"
            @on-ok="drop"
            v-if="!(isClone || isNew) && $can('delete', 'facts')"
          >
            <i-button type="error" :disabled="busy">
              <FFIcon type="remove" />Delete
            </i-button>
          </Poptip>

          <!-- <i-button @click="debug" :disabled="busy">
            <FFIcon type="terminal" /> Debug</i-button
          >-->
        </i-form-item>
      </i-form>
    </i-col>
    <LoadingSpinner :loading="loading"></LoadingSpinner>
  </Row>
</template>

<script>
import _ from "lodash";
import axios from "axios";

import database from "@/database";
import DynamicTags from "@/components/dynamic_tags";
import CountrySelect from "@/components/country_select";
import AgeDisaggregatedData from "@/components/age_disaggregated_data";
import ConflictTypeDisaggregatedData from "@/components/conflict_type_disaggregated_data";
import StrataDisaggregatedData from "@/components/strata_disaggregated_data";
import AccuracyDatePicker from "@/components/accuracy_date_picker";
// import FigureFields from "@/components/figure_fields";
import EventAssociation from "@/components/event_association";
import MasterFactAssociation from "@/components/master_fact_association.vue";
import FactsTable from "@/facts/table.vue";
import DocumentAssociation from "@/facts/document_association";
import ConfidenceAssessmentForm from "@/facts/confidence_assessment_form";
import GeolocationSelect from "@/components/geolocation_select";
import HouseholdSize from "@/components/household_size";
import Reviews from "@/components/reviews";
import GroupSelect from "@/components/GroupSelect";
import ThemeSelect from "@/components/ThemeSelect";
import { isNumber } from "@/utils/common";

import TabularDataForm from "./tabular_data";
import TabularDatasetTable from "@/facts/TabularDatasetTable.vue";
import SubFactsTable from "@/facts/SubFactsTable.vue";
import FactsTypeSelect from "@/components/fact_type_select.vue";

export default {
  components: {
    CountrySelect,
    DynamicTags,
    AccuracyDatePicker,
    // FigureFields,
    AgeDisaggregatedData,
    ConflictTypeDisaggregatedData,
    StrataDisaggregatedData,
    EventAssociation,
    MasterFactAssociation,
    FactsTable,
    ConfidenceAssessmentForm,
    DocumentAssociation,
    GeolocationSelect,
    HouseholdSize,
    Reviews,
    TabularDataForm,
    GroupSelect,
    ThemeSelect,
    TabularDatasetTable,
    SubFactsTable,
    FactsTypeSelect,
  },
  props: ["id", "document_id", "isClone"],
  data() {
    return {
      loading: false,
      activeTab: 0,
      status: null,
      selectedTabularRecords: [],
      searchTabularRecords: [],
      stockTotalSum: 0,
      fact: {
        id: this.id,
        name: "",
        serial_no: "",
        idu: false,
        iso3: "",
        year: null,
        household_size: {},
        start_date: {},
        end_date: {},
        groups: [],
        themes: [],
        data: {
          reported_figure: 0,
          unit: "Person",
          figure: 0,
          start_date: { date: null, accuracy: null },
          end_date: { date: null, accuracy: null },
          stock_date: { date: null, accuracy: null },
          conflict_type_disaggregated_data: {},
        },
        locations: {
          centroid: [],
          locations: [],
        },
        document_id: null,
        event_id: null,
        parent_id: null,
        confidence_assessment: null,
      },
      confidenceAssessmentOn:
        this.fact && this.fact.confidence_assessment !== null,
      clondedFact: {
        id: null,
        serial_no: null,
      },
      busy: false,
    };
  },
  watch: {
    id() {
      this.load();
    },
    fact() {
      this.confidenceAssessmentOn =
        this.fact && this.fact.confidence_assessment !== null;
    },
  },
  computed: {
    locked() {
      return (
        this.status &&
        (this.status.status === "Signed Off" ||
          this.status.status === "Locked Down")
      );
    },
    isNew() {
      return !this.fact.id || this.isClone;
    },
    isMaster() {
      return !this.fact.document_id;
    },
    factType() {
      if (!this.fact.document_id) {
        return "Master Fact";
      } else if (this.fact.parent_id !== null) {
        return "Sub-Fact";
      }
      return "Fact";
    },
    years() {
      const currYear = new Date().getFullYear();
      return _.range(currYear, 1950, -1);
    },
    isFlowType() {
      return _.includes(this.fact.type, "Flow");
    },
    // TODO: Implement fact full name
    fullname() {
      return this.fact.name;
    },
    breadcrumb() {
      if (this.isClone) {
        return `Clone ${this.factType} (${this.clondedFact.gsn})`;
      } else if (this.isNew) {
        return "New";
      }

      return this.fact.gsn;
    },
    title() {
      if (this.isClone) {
        return `Clone ${this.factType} (${this.clondedFact.gsn})`;
      } else if (this.isNew) {
        return `New ${this.factType}`;
      } else {
        return `Edit ${this.factType}`;
      }
    },
    sample() {
      console.log("sample called");
      return "sample";
    },
    rules() {
      let rules = {
        name: [
          {
            required: true,
            message: "Name is required",
            trigger: "blur",
          },
          {
            min: 3,
            message: "Length should be more than 3",
            trigger: "blur",
          },
        ],
        year: {
          required: true,
          type: "integer",
          message: "Year is required",
        },
        iso3: {
          required: true,
          message: "Country type is required",
        },
        displacement_type: {
          required: true,
          message: "Displacement type is required",
        },
        type: {
          required: true,
          message: "Fact type is required",
        },
        "data.reported_figure": {
          required: true,
          type: "integer",
          min: 0,
          message: "Reported figure is required",
        },
        "data.figure": {
          required: true,
          type: "integer",
          min: 0,
          message: "Reported figure is required",
        },
        "data.unit": {
          required: true,
          message: "Unit is required",
        },
        "data.term": {
          required: true,
          message: "Term is required",
        },
        "data.start_date.date": {
          required: true,
          message: "Start date is required",
        },
        "data.stock_date.date": {
          required: true,
          message: "Stock date is required",
        },
      };

      return rules;
    },
  },
  created() {
    if (this.id) {
      this.load();
    } else if (this.document_id) {
      this.fact.document_id = this.document_id;
    }
  },
  methods: {
    debug() {
      console.log(this.pack(this.fact));
      window.EventBus.$emit("VALIDATE_DATA");
    },

    unpack(fact) {
      fact.groups = fact.groups || [];
      fact.themes = fact.themes || [];
      fact.data = fact.data || {};
      if (typeof fact.data.location === "string") {
        fact.data.location = fact.data.location.split(",");
      }
      fact.data.location = fact.data.location || [];
      fact.data.conflict_type_disaggregated_data =
        fact.data.conflict_type_disaggregated_data || {};
      fact.locations = fact.locations || {
        centroid: [],
        locations: [],
      };

      if (this.isClone) {
        this.clondedFact.id = fact.id;
        this.clondedFact.serial_no = fact.serial_no;
        this.clondedFact.gsn = fact.gsn;
        this.clondedFact.name = fact.name;
        delete fact.id;
        delete fact.serial_no;
        delete fact.gsn;
        fact.name = `Clone ${this.clondedFact.gsn} - ${fact.name}`;
      }

      return fact;
    },

    load() {
      if (this.id !== null) {
        this.loading = true;
        this.$Loading.start();
        database
          .load("facts", this.id, "*,tabular_datasets_records_to_facts(*)")
          .then(response => {
            this.selectedTabularRecords =
              response.data.tabular_datasets_records_to_facts;
            delete response.data.tabular_datasets_records_to_facts;
            this.fact = this.unpack(response.data);
            this.loadingSuccess("Fact has been loaded");
            this.activeTab = 0;
            this.loading = false;

            // get Fact Records
            // this.getFactRecords(tabular_datasets_records_to_facts, () => {
            //   this.loadingSuccess("Facts records Loaded");
            // });
          })
          .catch(error => {
            this.fact.id = null;
            console.error(error);
            this.loadingError("Fact has not been loaded");
            this.$Loading.error();
            this.loading = false;
          });

        database
          .search(
            "facts_status_view",
            { entity_id: `eq.${this.id}` },
            { headers: { Accept: "application/vnd.pgrst.object+json" } }
          )
          .then(response => {
            this.status = response.data;
          });
      }
    },

    pack() {
      let fact = _.cloneDeep(this.fact);
      fact.groups = JSON.stringify(fact.groups)
        .replace("[", "{")
        .replace("]", "}");
      fact.themes = JSON.stringify(fact.themes)
        .replace("[", "{")
        .replace("]", "}");
      fact.confidence_assessment = null;
      if (this.confidenceAssessmentOn) {
        fact.confidence_assessment = this.fact.confidence_assessment;
      }
      delete fact.tabular_datasets_records_to_facts;
      return fact;
    },

    // deleteFactRecords(callback) {
    //   if (!this.id) {
    //     callback();
    //     return;
    //   }
    //   axios
    //     .delete("/psql/tabular_datasets_records_to_facts", {
    //       params: {
    //         f_id: `eq.${this.id}`,
    //       },
    //     })
    //     .then(() => {
    //       callback();
    //     })
    //     .catch(error => {
    //       console.error(error);
    //     });
    // },

    getFactRecords(factRecordMapping, callback) {
      axios
        .get("/psql/tabular_datasets_records", {
          params: {
            id: `in.(${factRecordMapping.map(x => x.tdr_id).join(",")})`,
            select: "*,tabular_datasets(id, name)",
          },
        })
        .then(response => {
          this.searchTabularRecords = response.data.map(datum => ({
            ...datum,
            start_date: datum.start_date && new Date(datum.start_date),
            end_date: datum.end_date && new Date(datum.end_date),
            dataset_name: datum.tabular_datasets.name,
          }));

          this.calculateStockTotal(response.data);
          this.selectedTabularRecords = this.searchTabularRecords.map(x => ({
            f_id: this.id,
            tdr_id: x.id,
          }));
          callback();
        })
        .catch(() => {
          this.loadingError("Fact Record could not be loaded");
        });
    },

    saveFactRecords() {
      this.deleteFactRecords(() => {
        database
          .bulkSave(
            "tabular_datasets_records_to_facts",
            this.selectedTabularRecords,
            {}
          )
          .then(() => {
            this.loadingSuccess("Facts records Updated");
            callback();
          })
          .catch(error => {
            console.error(error);
            const { response: err } = error;
            this.loadingError(
              `Failed to save dataset records: ${err.data.message}`
            );
          });
      });
    },

    calculateStockTotal(data) {
      this.stockTotalSum = data.reduce((acc, x) => {
        if (isNumber(+x.stock_total)) {
          acc += +x.stock_total;
        }
        return acc;
      }, 0);
    },

    updateSelectedRecords(data) {
      this.calculateStockTotal(data);
      this.selectedTabularRecords = data.map(x => ({
        f_id: this.id,
        tdr_id: x.id,
      }));
    },

    // Save the fact to the database
    save() {
      if (!this.$can("manage", "facts") || this.locked) {
        this.$Message.error("User is not permitted");
        return;
      }
      window.EventBus.$emit("VALIDATE_DATA");
      this.$refs["form"].validate(valid => {
        if (valid) {
          if (
            !!this.fact.data.end_date && !!this.fact.data.end_date.date &&
            this.fact.data.end_date.date < this.fact.data.start_date.date
          ) {
            this.$Notice.error({
              title: "Date error",
              desc: "End date can't be before start date.",
            });
            return;
          }

          this.$Loading.start();
          database
            .save("facts", this.pack(this.fact), {})
            .then(response => {
              const action = this.isNew ? "created" : "updated";
              this.loadingSuccess(`Fact has been ${action}`);
              // this.saveFactRecords(() => {
              this.$router.push(`/facts/${response.data.id}`);
              // });
            })
            .catch(err => {
              console.error(err.response);
              const action = this.isNew ? "create" : "update";
              this.loadingError(`Failed to ${action} fact`);
            });
        } else {
          this.loadingError("Form error");
        }
      });
    },

    // Drop a fact from the database
    drop() {
      if (!this.$can("delete", "facts")) {
        this.$Message.error("User is not permitted");
        return;
      }
      this.$Modal.confirm({
        title: "Delete Fact",
        content: "This action will permanently delete this fact, are you sure?",
        onOk: () => {
          this.$Loading.start();
          database
            .delete("facts", this.fact.id)
            .then(() => {
              this.loadingSuccess("Successfully deleted");
              this.$router.push(`/facts`);
            })
            .catch(err => {
              console.error(err.response);
              this.loadingError(
                "Failed to delete: " + err.response.data.message
              );
            });
        },
      });
    },
    calculate() {
      if (this.fact.data.unit === "Household") {
        this.fact.data.figure = Math.round(
          this.fact.data.reported_figure * this.fact.household_size.figure
        );
      } else {
        this.fact.data.figure = this.fact.data.reported_figure;
      }
    },
  },
};
</script>

<style lang="css">
.form-hint {
  width: 100%;
  text-align: right;
  padding-right: 4px;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
